import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import {
  forceSimulation,
  forceManyBody,
  forceCollide,
  forceRadial,
  forceLink,
  forceCenter,
} from 'd3-force';
import { select } from 'd3-selection';
import { drag as d3drag } from 'd3-drag';
import { zoom as d3zoom, zoomIdentity } from 'd3-zoom';
import './MemorySpace.css';

/**
 * Interactive force-directed view of the memory space for one verification run.
 *
 * Relevance is encoded as distance: the claim is pinned at the centre and each
 * memory orbits at a radius derived from its similarity score, so strong matches
 * physically cluster around the claim. A dashed "relevance horizon" marks the
 * filtering threshold and rejected neighbours sit outside it, which makes the
 * filtering step visible rather than implied.
 *
 * The simulation stays alive so nodes can be dragged: a released node settles
 * back under the radial force, letting a user pull a cluster apart to read it
 * and then watch it reassemble.
 */

const TYPE_STYLE = {
  fact: { fill: '#8b5cf6', label: 'fact' },
  episode: { fill: '#3b82f6', label: 'episode' },
  artifact: { fill: '#10b981', label: 'article' },
  lesson: { fill: '#f59e0b', label: 'lesson' },
  procedure: { fill: '#f59e0b', label: 'procedure' },
  claim: { fill: '#e5e7eb', label: 'claim' },
  filtered: { fill: '#4b5563', label: 'filtered out' },
};

const WIDTH = 760;
const HEIGHT = 440;
const HORIZON = 155;

function truncate(text, max) {
  const value = String(text || '');
  return value.length > max ? `${value.slice(0, max - 1)}\u2026` : value;
}

const MemorySpace = ({ memory }) => {
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const nodesRef = useRef([]);
  const simRef = useRef(null);
  const zoomRef = useRef(null);
  const [selected, setSelected] = useState(null);

  const model = useMemo(() => {
    if (!memory?.enabled) return null;

    const recalled = memory.recalled || [];
    const lessons = memory.lessons || [];
    const stored = memory.write?.created || [];
    const articleSources = memory.articles?.sources || [];
    const filteredCount = memory.recall_filtered || 0;

    const nodes = [
      {
        id: 'claim',
        group: 'claim',
        label: 'This claim',
        full: 'The claim being verified in this run',
        r: 21,
        radius: 0,
      },
    ];
    const links = [];

    recalled.forEach((item, i) => {
      const score = Math.max(0, Math.min(1, item.score || 0));
      const id = `r${i}`;
      nodes.push({
        id,
        group: item.type || 'fact',
        label: truncate(item.text, 90),
        full: item.text,
        r: 9 + score * 5,
        // Strong matches orbit close; weak ones sit out near the horizon.
        radius: 58 + (1 - score) * (HORIZON - 70),
        badge: `${Math.round(score * 100)}%`,
      });
      links.push({ source: 'claim', target: id, kind: 'recall' });
    });

    articleSources.slice(0, 8).forEach((src, i) => {
      const id = `a${i}`;
      nodes.push({
        id,
        group: 'artifact',
        label: truncate(src.title, 70),
        full: `${src.title}\n${src.url}`,
        r: 8,
        radius: HORIZON * 0.74,
        badge: 'ingested',
        fresh: true,
      });
      links.push({ source: 'claim', target: id, kind: 'ingest' });
    });

    stored.slice(0, 6).forEach((item, i) => {
      const id = `w${i}`;
      nodes.push({
        id,
        group: item.type || 'fact',
        label: truncate(item.text, 80),
        full: item.text,
        r: 10,
        radius: 82,
        badge: 'new',
        fresh: true,
      });
      links.push({ source: 'claim', target: id, kind: 'write' });
    });

    lessons.slice(0, 4).forEach((item, i) => {
      const id = `l${i}`;
      nodes.push({
        id,
        group: 'lesson',
        label: truncate(item.text, 80),
        full: item.text,
        r: 8,
        radius: 100,
        badge: item.observation_count ? `${item.observation_count}x` : 'lesson',
      });
      links.push({ source: 'claim', target: id, kind: 'lesson' });
    });

    for (let i = 0; i < Math.min(filteredCount, 8); i += 1) {
      nodes.push({
        id: `f${i}`,
        group: 'filtered',
        label: 'below threshold',
        full: 'Nearest-neighbour result rejected by the relevance filter',
        r: 5,
        radius: HORIZON + 46 + (i % 3) * 18,
        muted: true,
      });
    }

    return { nodes, links, filteredCount };
  }, [memory]);

  // Simulation, drag and pan/zoom. The DOM is updated imperatively on tick so
  // React is not re-rendering sixty times a second.
  useEffect(() => {
    if (!model || !svgRef.current) return undefined;

    const svg = svgRef.current;
    const nodes = model.nodes.map((n) => ({ ...n }));
    const links = model.links.map((l) => ({ ...l }));
    nodesRef.current = nodes;

    const claim = nodes.find((n) => n.id === 'claim');
    claim.fx = WIDTH / 2;
    claim.fy = HEIGHT / 2;

    const simulation = forceSimulation(nodes)
      .force('charge', forceManyBody().strength(-130))
      .force('center', forceCenter(WIDTH / 2, HEIGHT / 2))
      .force('collide', forceCollide().radius((d) => d.r + 13))
      .force(
        'radial',
        forceRadial((d) => d.radius || 0, WIDTH / 2, HEIGHT / 2).strength(0.8)
      )
      .force(
        'link',
        forceLink(links)
          .id((d) => d.id)
          .distance((d) => d.target.radius || 80)
          .strength(0.1)
      )
      // A low alphaMin keeps the layout gently alive so dragged nodes glide back.
      .alphaMin(0.002)
      .alphaDecay(0.02);

    simRef.current = simulation;

    const nodeEls = new Map();
    const linkEls = new Map();
    nodes.forEach((n) => nodeEls.set(n.id, svg.querySelector(`[data-node="${n.id}"]`)));
    links.forEach((l, i) => linkEls.set(i, svg.querySelector(`[data-link="${i}"]`)));

    simulation.on('tick', () => {
      nodes.forEach((n) => {
        // Clamp inside the frame so a hard drag cannot lose a node offscreen.
        n.x = Math.max(n.r + 4, Math.min(WIDTH - n.r - 4, n.x));
        n.y = Math.max(n.r + 4, Math.min(HEIGHT - n.r - 4, n.y));
        const el = nodeEls.get(n.id);
        if (el) el.setAttribute('transform', `translate(${n.x},${n.y})`);
      });
      links.forEach((l, i) => {
        const el = linkEls.get(i);
        if (!el) return;
        el.setAttribute('x1', l.source.x);
        el.setAttribute('y1', l.source.y);
        el.setAttribute('x2', l.target.x);
        el.setAttribute('y2', l.target.y);
      });
    });

    // Drag pins a node while held and releases it back to the simulation after.
    // The claim stays pinned: it is the anchor every radius is measured from.
    const dragBehaviour = d3drag()
      .container(svg)
      .subject((event) => {
        const id = event.sourceEvent?.target?.closest?.('[data-node]')?.dataset?.node;
        return nodes.find((n) => n.id === id) || null;
      })
      .on('start', (event) => {
        if (!event.subject) return;
        simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      })
      .on('drag', (event) => {
        if (!event.subject) return;
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      })
      .on('end', (event) => {
        if (!event.subject) return;
        simulation.alphaTarget(0);
        if (event.subject.id !== 'claim') {
          event.subject.fx = null;
          event.subject.fy = null;
        }
      });

    const svgSel = select(svg);
    svgSel.selectAll('.memory-node').call(dragBehaviour);

    // Pan/zoom is filtered to the background so dragging a node never pans the
    // canvas, and wheel zoom is off so the page keeps scrolling normally.
    const zoomBehaviour = d3zoom()
      .scaleExtent([0.5, 2.4])
      .filter((event) => event.type !== 'wheel' && !event.target.closest?.('[data-node]'))
      .on('zoom', (event) => {
        if (viewportRef.current) {
          viewportRef.current.setAttribute('transform', event.transform.toString());
        }
      });
    svgSel.call(zoomBehaviour);
    zoomRef.current = { svgSel, zoomBehaviour };

    return () => {
      simulation.stop();
      svgSel.on('.zoom', null);
    };
  }, [model]);

  const reheat = useCallback(() => {
    const sim = simRef.current;
    if (!sim) return;
    nodesRef.current.forEach((n) => {
      if (n.id !== 'claim') {
        n.fx = null;
        n.fy = null;
      }
    });
    sim.alpha(0.9).restart();
    const z = zoomRef.current;
    if (z) {
      z.svgSel.transition().duration(280).call(z.zoomBehaviour.transform, zoomIdentity);
    }
  }, []);

  if (!model) return null;

  const { nodes, links, filteredCount } = model;
  const selectedNode = selected ? nodes.find((n) => n.id === selected) : null;

  return (
    <div className="memory-space">
      <div className="memory-space-head">
        <h4>Memory Space</h4>
        <span className="memory-space-hint">
          drag to explore · distance = relevance · dashed ring = filter threshold
        </span>
        <button type="button" className="ms-reset" onClick={reheat}>
          Re-centre
        </button>
      </div>

      <svg
        ref={svgRef}
        className="memory-space-svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Memory space graph"
      >
        <defs>
          <radialGradient id="msCore">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </radialGradient>
          <filter id="msGlow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g ref={viewportRef}>
          <circle cx={WIDTH / 2} cy={HEIGHT / 2} r={HORIZON} fill="url(#msCore)" />
          <circle className="memory-horizon" cx={WIDTH / 2} cy={HEIGHT / 2} r={HORIZON} />
          <text
            className="memory-horizon-label"
            x={WIDTH / 2 + HORIZON + 6}
            y={HEIGHT / 2 - 6}
          >
            relevance horizon
          </text>

          {links.map((link, i) => (
            <line key={i} data-link={i} className={`memory-link ${link.kind}`} />
          ))}

          {nodes.map((node) => {
            const style = TYPE_STYLE[node.group] || TYPE_STYLE.fact;
            const isSelected = selected === node.id;
            return (
              <g
                key={node.id}
                data-node={node.id}
                className={`memory-node ${node.muted ? 'muted' : ''} ${
                  node.fresh ? 'fresh' : ''
                } ${isSelected ? 'selected' : ''}`}
                onClick={() => setSelected(isSelected ? null : node.id)}
              >
                <circle
                  r={node.r}
                  fill={style.fill}
                  fillOpacity={node.muted ? 0.25 : 0.9}
                  stroke={isSelected ? '#f9fafb' : node.muted ? '#4b5563' : '#0b1120'}
                  strokeWidth={isSelected ? 2.5 : 1.5}
                  filter={node.id === 'claim' ? 'url(#msGlow)' : undefined}
                />
                {node.badge && !node.muted && (
                  <text className="memory-node-badge" y={-node.r - 5}>
                    {node.badge}
                  </text>
                )}
                <text className="memory-node-label" y={node.r + 12}>
                  {truncate(node.label, 26)}
                </text>
                <title>{`${style.label}: ${node.full || node.label}`}</title>
              </g>
            );
          })}
        </g>
      </svg>

      {selectedNode && (
        <div className="ms-detail">
          <span
            className="ms-detail-type"
            style={{ background: (TYPE_STYLE[selectedNode.group] || TYPE_STYLE.fact).fill }}
          >
            {(TYPE_STYLE[selectedNode.group] || TYPE_STYLE.fact).label}
          </span>
          <p>{selectedNode.full || selectedNode.label}</p>
        </div>
      )}

      <div className="memory-space-legend">
        {['fact', 'episode', 'artifact', 'lesson'].map((key) => (
          <span key={key} className="ms-legend">
            <i style={{ background: TYPE_STYLE[key].fill }} />
            {TYPE_STYLE[key].label}
          </span>
        ))}
        {filteredCount > 0 && (
          <span className="ms-legend muted">
            <i style={{ background: TYPE_STYLE.filtered.fill }} />
            {filteredCount} filtered out
          </span>
        )}
      </div>
    </div>
  );
};

export default MemorySpace;
